import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CheckoutSuccessComponentDef } from '@df/checkout/checkout-success.component.def';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { IAddressData } from '@df/session/api/address-book/address';
import { Customer } from '@df/session/api/customer';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { BloomreachService } from '../module/bloomreach/bloomreach.service';
import { SagepayCloudPaymentRenewPaymentComponent } from '../module/sagepay/cloud-payment/sagepay-cloud-payment-renew-payment.component';
import { ActionComponent } from '../ui/api/action.component';
import { ResultComponent } from '../ui/api/result.component';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
import { MuiInputDirective } from '../ui/form/mui-input.directive';
import { CheckoutSuccessContentBlockComponent } from './checkout-success-content-block.component';
import { CheckoutSuccessNewsletterComponent } from './checkout-success-newsletter.component';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessCustomerData {
            subscribed?: boolean;
        }
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Api.Checkout.Success {
        interface ISuccessData {
            billing: IAddressData;
            shipping: IAddressData;
            sccp_payment_reference?: string;
            gift_receipt?: boolean;
        }

        interface ISuccessItemData {
            price: number;
        }

        interface IShippingMethod {
            code: string;
            description?: string;
        }

        interface IPaymentMethod {
            code: string | number;
            title: string;
            description?: string;
        }
    }

    namespace Nexus.Lib.Customer.Order {
        interface HistoryOrderPaymentMethod {
            title?: string;
        }
    }
}

@Component({
    selector: 'checkout-success',
    styleUrl: './checkout-success.scss',
    templateUrl: './checkout-success.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ActionComponent,
        CheckoutSuccessContentBlockComponent,
        CheckoutSuccessNewsletterComponent,
        CommonModule,
        FormsModule,
        HrefComponent,
        ICmsOutletBlockComponent,
        IfSizeDirective,
        InputWrapBodyDirective,
        InputWrapComponent,
        MuiInputDirective,
        PriceComponent,
        ResultComponent,
        RouterModule,
        SagepayCloudPaymentRenewPaymentComponent,
        TranslatePipe
    ]
})
export class CheckoutSuccessComponent extends CheckoutSuccessComponentDef implements OnInit {
    protected consentStatus = new Status();
    protected consentResult: boolean = false;
    protected showForm = false;
    protected isRegistered = false;

    readonly client = inject(Client);
    protected readonly customer = inject(Customer);
    protected bloomreachService = inject(BloomreachService);

    @ViewChild(CheckoutSuccessNewsletterComponent)
    private readonly checkoutSuccessNewsletterComponent?: CheckoutSuccessNewsletterComponent;

    override ngOnInit() {
        super.ngOnInit();

        this.checkConsent(this.data.customer.email);

        this.changeDetectorRef.detectChanges();
    }

    get paymentMethod(): string {
        return this.data.payment_method?.title || this.data.payment_method?.description || '';
    }

    protected get showSubscriptionOptIn(): boolean {
        return !this.consentResult && !this.consentStatus.busy;
    }

    protected get bloomreachLink(): string | undefined {
        // must be `exponea` to be compatible with old data
        return this.customer.model.data?.exponea;
    }

    protected checkConsent(email: string): void {
        this.bloomreachService.checkConsent(this.consentStatus, email, result => {
            this.consentResult = !!result.consent;

            this.changeDetectorRef.detectChanges();
        });
    }

    protected openForm(): void {
        this.showForm = true;
    }

    protected override onRegister() {
        this.isRegistered = true;
    }

    override register(): void {
        if (this.checkoutSuccessNewsletterComponent) {
            this.checkoutSuccessNewsletterComponent.submitAsync().finally(() => super.register());
        } else {
            super.register();
        }
    }
}
